import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';

import { getDashboardPanelTokens, rem } from '../dashboardPanelTokens';
import { fullBleed } from '../../../../components/panels/panelLayout';
import { CoverageEyebrow } from './CoverageParts';
import { TilePrimaryButton } from './CoverageActions';
import type { CoverageKind } from './coverage.types';

interface CoverageBandProps {
  readonly kind: CoverageKind;
  readonly headline: string;
  readonly detail: string;
  readonly actionLabel: string;
  readonly actionHref: string;
}

/**
 * A band across the foot of the tile for something attached to the coverage
 * above it -- a renewal quote, a next term already bound, a set of options.
 *
 * Attached rather than a sibling: nested inside the policy in the prototype's
 * data it was invisible to anything that iterated, and rendered as a separate
 * row it read as unrelated coverage. A band says "this belongs to that".
 */
export function CoverageBand({
  kind,
  headline,
  detail,
  actionLabel,
  actionHref,
}: Readonly<CoverageBandProps>) {
  const { palette } = useTheme();
  const tokens = getDashboardPanelTokens(palette.mode);

  return (
    <Box
      data-qa="COVERAGE_BAND"
      sx={{
        ...fullBleed,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: rem(16),
        marginTop: rem(20),
        marginBottom: `-${rem(24)}`,
        paddingTop: rem(14),
        paddingBottom: rem(14),
        borderTop: `1px solid ${tokens.line}`,
        backgroundColor: tokens.brandSurface,
      }}
    >
      <Box sx={{ flex: '1 1 auto', minWidth: rem(220) }}>
        <CoverageEyebrow kind={kind} />
        <Typography
          sx={{
            marginTop: rem(4),
            fontSize: rem(13),
            fontWeight: 700,
            lineHeight: 1.4,
            color: tokens.ink,
          }}
        >
          {headline}
        </Typography>
        <Typography
          sx={{
            marginTop: rem(2),
            fontSize: rem(13),
            lineHeight: 1.45,
            color: tokens.inkBody,
          }}
        >
          {detail}
        </Typography>
      </Box>

      <TilePrimaryButton component={Link} to={actionHref}>
        {actionLabel}
      </TilePrimaryButton>
    </Box>
  );
}
